<template>
  <div class="bill-tick">
    <div class="page-header">
      <div class="page-title">
        <div class="title-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 19V5l11-7v14M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm11-2c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h1>分笔成交明细</h1>
        <div class="title-decoration"></div>
      </div>
      <p class="page-subtitle">股票分笔成交数据，逐笔分析交易细节</p>
    </div>

    <div class="filter-bar">
      <div class="filter-group">
        <label class="filter-label">股票代码</label>
        <el-select
          v-model="stockCode"
          placeholder="请选择股票"
          filterable
          class="stock-select"
          v-loading="stockListLoading"
        >
          <el-option
            v-for="item in stockList"
            :key="item.stock_code"
            :label="`${item.stock_name} (${item.stock_code})`"
            :value="item.stock_code"
          >
          </el-option>
        </el-select>
      </div>

      <div class="filter-group">
        <label class="filter-label">日期</label>
        <el-date-picker
          v-model="date"
          type="date"
          placeholder="选择日期，留空则获取最新"
          value-format="yyyy-MM-dd"
          class="date-picker"
        >
        </el-date-picker>
      </div>

      <div class="filter-group">
        <label class="filter-label">批量模式</label>
        <el-switch v-model="batchMode" active-text="开启"></el-switch>
      </div>

      <div v-if="batchMode" class="filter-group">
        <label class="filter-label">开始日期</label>
        <el-date-picker
          v-model="startDate"
          type="date"
          placeholder="选择开始日期"
          value-format="yyyy-MM-dd"
          class="date-picker"
        >
        </el-date-picker>
      </div>

      <div v-if="batchMode" class="filter-group">
        <label class="filter-label">结束日期</label>
        <el-date-picker
          v-model="endDate"
          type="date"
          placeholder="选择结束日期"
          value-format="yyyy-MM-dd"
          class="date-picker"
        >
        </el-date-picker>
      </div>

      <div class="button-group">
        <el-button type="primary" @click="handleFetch" :loading="fetching">
          <i class="el-icon-download"></i>
          获取数据
        </el-button>
        <el-button @click="resetFilters">
          <i class="el-icon-refresh-left"></i>
          重置
        </el-button>
      </div>
    </div>

    <div v-if="chartData || batchData.length > 0" class="tick-content">
      <!-- 统计信息 -->
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-label">股票</div>
          <div class="stat-value">{{ currentStockName || stockCode }}</div>
        </div>
        <div class="stat-card" v-if="!batchMode && chartData">
          <div class="stat-label">日期</div>
          <div class="stat-value">{{ chartData.date || '最新' }}</div>
        </div>
        <div class="stat-card" v-if="!batchMode && chartData">
          <div class="stat-label">交易明细条数</div>
          <div class="stat-value">{{ chartData.raw_count || 0 }} 条</div>
        </div>
        <div class="stat-card" v-if="!batchMode && chartData">
          <div class="stat-label">价格方向组合</div>
          <div class="stat-value">{{ chartData.series_count }} 组</div>
        </div>
        <div class="stat-card" v-if="!batchMode">
          <div class="stat-label">总成交量</div>
          <div class="stat-value">{{ formatVolume(totalVolume) }} 手</div>
        </div>
        <div class="stat-card" v-if="batchMode">
          <div class="stat-label">交易天数</div>
          <div class="stat-value">{{ batchData.length }} 天</div>
        </div>
        <div class="stat-card" v-if="batchMode">
          <div class="stat-label">总成交笔数</div>
          <div class="stat-value">{{ totalBatchCount }} 笔</div>
        </div>
      </div>

      <!-- 批量模式下的数据展示 -->
      <div v-if="batchMode" class="batch-section">
        <div class="date-tabs">
          <el-radio-group v-model="selectedDateIndex" size="small">
            <el-radio-button
              v-for="(day, index) in batchData"
              :key="day.date"
              :label="index"
            >
              {{ day.date }} ({{ day.count }}笔)
            </el-radio-button>
          </el-radio-group>
        </div>

        <div v-if="selectedDayData" class="day-stats">
          <span class="day-stat-item">成交量: {{ formatVolume(selectedDayData.total_volume) }} 手</span>
          <span class="day-stat-item">成交额: {{ formatAmount(selectedDayData.total_amount) }} 元</span>
        </div>
      </div>

      <!-- 合并后的总量图表 -->
      <div v-if="chartData || (batchMode && selectedDayTransactions.length > 0)" class="charts-section">
        <div class="section-title">分时成交量走势（合并所有价格）</div>
        <div class="charts-row">
          <div class="chart-item" ref="mergedVolumeChartContainer"></div>
        </div>
      </div>

      <!-- 成交量图表（三个方向） -->
      <div v-if="chartData || (batchMode && selectedDayTransactions.length > 0)" class="charts-section">
        <div class="section-title">成交量走势（同一时刻同一价格累计）</div>
        <div class="charts-row">
          <div class="chart-item" ref="buyChartContainer"></div>
          <div class="chart-item" ref="sellChartContainer"></div>
          <div class="chart-item" ref="neutralChartContainer"></div>
        </div>
      </div>

      <!-- 成交次数图表（三个方向） -->
      <div v-if="chartData || (batchMode && selectedDayTransactions.length > 0)" class="charts-section">
        <div class="section-title">成交次数走势（同一时刻同一价格的成交笔数）</div>
        <div class="charts-row">
          <div class="chart-item" ref="buyCountChartContainer"></div>
          <div class="chart-item" ref="sellCountChartContainer"></div>
          <div class="chart-item" ref="neutralCountChartContainer"></div>
        </div>
      </div>

      <!-- 数据表格 -->
      <div v-if="(chartData && displayData.length > 0) || (batchMode && displayData.length > 0)" class="table-section">
        <div class="table-header">
          <h3>详细数据</h3>
          <div class="table-filter">
            <span class="filter-label-text">性质筛选:</span>
            <el-radio-group v-model="directionFilter" size="small">
              <el-radio-button label="全部">全部</el-radio-button>
              <el-radio-button label="买盘">买盘</el-radio-button>
              <el-radio-button label="卖盘">卖盘</el-radio-button>
              <el-radio-button label="中性盘">中性盘</el-radio-button>
            </el-radio-group>
          </div>
        </div>
        <el-table
          :data="displayData"
          border
          stripe
          style="width: 100%"
          :height="500"
        >
          <el-table-column
            prop="time"
            label="时间"
            width="180"
          ></el-table-column>
          <el-table-column prop="price" label="价格" width="100" align="right">
            <template slot-scope="scope">
              {{ scope.row.price ? Number(scope.row.price).toFixed(2) : '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="volume" label="成交量(手)" width="130" align="right">
            <template slot-scope="scope">
              {{ scope.row.volume || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="count" label="成交次数(笔)" width="130" align="right">
            <template slot-scope="scope">
              {{ scope.row.count || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="direction" label="性质" width="100" align="center">
            <template slot-scope="scope">
              <el-tag
                :type="getDirectionType(scope.row.direction)"
                size="small"
              >
                {{ scope.row.direction || '中性' }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>

        <el-pagination
          v-if="totalDisplayCount > pageSize"
          @current-change="handlePageChange"
          :current-page="currentPage"
          :page-size="pageSize"
          :total="totalDisplayCount"
          layout="total, prev, pager, next, jumper"
          class="pagination"
        ></el-pagination>
      </div>
    </div>

    <el-empty v-else-if="!fetching" description="请输入股票代码并获取数据"></el-empty>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'

export default {
  name: 'BillTick',
  data() {
    return {
      stockCode: '000001',
      date: '',
      batchMode: false,
      startDate: '',
      endDate: '',
      tickData: [],
      batchData: [],
      fetching: false,
      currentPage: 1,
      pageSize: 100,
      chartData: null,
      buyChart: null,
      sellChart: null,
      neutralChart: null,
      buyCountChart: null,
      sellCountChart: null,
      neutralCountChart: null,
      mergedVolumeChart: null,
      selectedDateIndex: 0,
      stockList: [],
      stockListLoading: false,
      directionFilter: '全部'
    }
  },
  mounted() {
    this.fetchStockList()
  },
  computed: {
    // 表格显示的数据：按时刻+价格+方向累计后的数据
    displayData() {
      if (this.batchMode) {
        return this.paginatedBatchData
      }

      if (!this.chartData || !this.chartData.times || !this.chartData.volume_series_data) {
        return []
      }

      const times = this.chartData.times
      const volumeSeriesData = this.chartData.volume_series_data
      const countSeriesData = this.chartData.count_series_data || []

      // 构建 count_series_data 的快速查找映射
      const countMap = {}
      countSeriesData.forEach(item => {
        countMap[item.series] = item.data || []
      })

      // 展开数据：每个时刻+系列组合作为一行
      const rows = []
      volumeSeriesData.forEach(seriesItem => {
        const seriesName = seriesItem.series
        const volumeData = seriesItem.data || []
        const countData = countMap[seriesName] || []

        // 解析价格和方向
        let price = ''
        let direction = ''
        const match = seriesName.match(/^([\d.]+)(买盘|卖盘|中性盘)$/)
        if (match) {
          price = match[1]
          direction = match[2]
        }

        times.forEach((time, index) => {
          const volume = volumeData[index] || 0
          const count = countData[index] || 0
          if (volume > 0 || count > 0) { // 显示有成交量或成交次数的数据
            rows.push({
              time: time,
              price: price,
              direction: direction,
              volume: volume,
              count: count
            })
          }
        })
      })

      // 按时刻排序
      rows.sort((a, b) => a.time.localeCompare(b.time))

      // 按性质筛选
      let filteredRows = rows
      if (this.directionFilter !== '全部') {
        filteredRows = rows.filter(row => row.direction === this.directionFilter)
      }

      // 分页
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return filteredRows.slice(start, end)
    },
    // 分组数据的总数（用于分页）
    totalDisplayCount() {
      if (this.batchMode) {
        const transactions = this.selectedDayTransactions
        return transactions.length
      }

      if (!this.chartData || !this.chartData.times || !this.chartData.volume_series_data) {
        return 0
      }

      const times = this.chartData.times
      const volumeSeriesData = this.chartData.volume_series_data
      const countSeriesData = this.chartData.count_series_data || []

      // 构建 count_series_data 的快速查找映射
      const countMap = {}
      countSeriesData.forEach(item => {
        countMap[item.series] = item.data || []
      })

      let count = 0
      volumeSeriesData.forEach(seriesItem => {
        const volumeData = seriesItem.data || []
        const countData = countMap[seriesItem.series] || []

        // 解析方向
        let direction = ''
        const match = seriesItem.series.match(/^([\d.]+)(买盘|卖盘|中性盘)$/)
        if (match) {
          direction = match[2]
        }

        // 如果筛选条件不是"全部"且方向不匹配，跳过
        if (this.directionFilter !== '全部' && direction !== this.directionFilter) {
          return
        }

        times.forEach((_, index) => {
          if ((volumeData[index] || 0) > 0 || (countData[index] || 0) > 0) count++
        })
      })
      return count
    },
    paginatedBatchData() {
      const transactions = this.selectedDayTransactions
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return transactions.slice(start, end)
    },
    selectedDayTransactions() {
      if (this.batchMode && this.batchData.length > 0 && this.selectedDateIndex < this.batchData.length) {
        return this.batchData[this.selectedDateIndex].transactions || []
      }
      return []
    },
    selectedDayData() {
      if (this.batchMode && this.batchData.length > 0 && this.selectedDateIndex < this.batchData.length) {
        return this.batchData[this.selectedDateIndex]
      }
      return null
    },
    totalVolume() {
      if (this.batchMode && this.selectedDayData) {
        return this.selectedDayData.total_volume || 0
      }
      if (!this.chartData || !this.chartData.volume_series_data) {
        return 0
      }
      // 计算总成交量 - 使用 volume_series_data
      return this.chartData.volume_series_data.reduce((sum, series) => {
        return sum + (series.data || []).reduce((s, v) => s + v, 0)
      }, 0)
    },
    totalAmount() {
      if (this.batchMode && this.selectedDayData) {
        return this.selectedDayData.total_amount || 0
      }
      return 0 // 后端没有返回成交额的累计数据
    },
    totalBatchCount() {
      return this.batchData.reduce((sum, day) => sum + (day.count || 0), 0)
    },
    // 当前选中股票的名称
    currentStockName() {
      const stock = this.stockList.find(item => item.stock_code === this.stockCode)
      return stock ? stock.stock_name : ''
    }
  },
  watch: {
    selectedDateIndex() {
      this.currentPage = 1
      this.$nextTick(() => {
        this.renderChart()
      })
    },
    directionFilter() {
      this.currentPage = 1
    }
  },
  methods: {
    // 获取股票列表
    async fetchStockList() {
      this.stockListLoading = true
      try {
        const response = await axios.get('/stock/info/list')
        if (response.data.status === 'success' && response.data.data) {
          this.stockList = response.data.data
        } else {
          this.stockList = []
        }
      } catch (error) {
        console.error('获取股票列表失败:', error)
        this.$message.error('获取股票列表失败')
        this.stockList = []
      } finally {
        this.stockListLoading = false
      }
    },

    // 日期格式转换 yyyy-MM-dd -> yyyyMMdd
    formatDateForBackend(dateStr) {
      if (!dateStr) return ''
      return dateStr.replace(/-/g, '')
    },

    async handleFetch() {
      if (!this.stockCode) {
        this.$message.warning('请选择股票')
        return
      }

      if (this.batchMode) {
        if (!this.startDate || !this.endDate) {
          this.$message.warning('批量模式下请提供开始和结束日期')
          return
        }
      }

      this.fetching = true
      try {
        let url, params

        if (this.batchMode) {
          url = '/quote/transactions/batch'
          params = {
            code: this.stockCode,
            start_date: this.formatDateForBackend(this.startDate),
            end_date: this.formatDateForBackend(this.endDate)
          }
        } else {
          url = '/quote/transactions'
          params = { code: this.stockCode }
          if (this.date) {
            params.date = this.formatDateForBackend(this.date)
          }
        }

        const response = await axios.get(url, { params })

        console.log('后端响应:', response.data)

        if (response.data.status === 'success') {
          if (this.batchMode) {
            this.batchData = response.data.data || []
            this.selectedDateIndex = 0
            this.$message.success(`成功获取 ${this.batchData.length} 天的数据`)
          } else {
            this.chartData = response.data
            this.$message.success(`成功获取 ${response.data.series_count} 个价格方向组合的数据`)
          }
          this.currentPage = 1
          this.$nextTick(() => {
            this.renderChart()
          })
        } else if (response.data.status === 'no_data') {
          this.$message.warning(response.data.message || '未获取到数据')
          this.chartData = null
        } else {
          this.$message.error(response.data.message || '获取数据失败')
          this.chartData = null
        }
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败: ' + (error.response?.data?.message || error.message))
        this.tickData = []
        this.batchData = []
      } finally {
        this.fetching = false
      }
    },

    renderChart() {
      if (!this.chartData || !this.chartData.times || this.chartData.times.length === 0) {
        console.log('数据为空，不渲染图表')
        return
      }

      const times = this.chartData.times
      const volumeSeriesData = this.chartData.volume_series_data || []
      const countSeriesData = this.chartData.count_series_data || []

      // 渲染合并后的总量图表
      this.renderMergedVolumeChart(times, volumeSeriesData)

      // 渲染成交量图表（买盘、卖盘、中性盘）
      this.renderVolumeCharts(times, volumeSeriesData)

      // 渲染成交次数图表（买盘、卖盘、中性盘）
      this.renderCountCharts(times, countSeriesData)

      window.addEventListener('resize', this.handleResize)
    },

    // 渲染合并后的总量图表
    renderMergedVolumeChart(times, seriesData) {
      const container = this.$refs.mergedVolumeChartContainer
      if (!container) {
        console.log('mergedVolumeChartContainer 不存在')
        return
      }

      if (this.mergedVolumeChart) {
        this.mergedVolumeChart.dispose()
      }

      this.mergedVolumeChart = echarts.init(container)

      // 按时刻合并所有价格的成交量
      const buyVolumes = []
      const sellVolumes = []
      const neutralVolumes = []

      times.forEach((_, timeIndex) => {
        let buyTotal = 0
        let sellTotal = 0
        let neutralTotal = 0

        seriesData.forEach(item => {
          const value = item.data[timeIndex] || 0
          if (item.series.includes('买盘')) {
            buyTotal += value
          } else if (item.series.includes('卖盘')) {
            sellTotal += value
          } else {
            neutralTotal += value
          }
        })

        buyVolumes.push(buyTotal)
        sellVolumes.push(sellTotal)
        neutralVolumes.push(neutralTotal)
      })

      const option = {
        title: {
          text: `${this.stockCode} 分时成交量走势（合并）${this.chartData.date ? ' - ' + this.chartData.date : ''}`,
          left: 'center',
          textStyle: { color: '#e2e8f0', fontSize: 14 }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          formatter: (params) => {
            if (!params || params.length === 0) return ''
            let result = `${params[0].axisValue}<br/>`
            params.forEach(param => {
              result += `${param.marker} ${param.seriesName}: ${param.value} 手<br/>`
            })
            return result
          }
        },
        legend: {
          data: ['买盘', '卖盘', '中性盘'],
          top: 25,
          textStyle: { color: '#f1f5f9', fontSize: 12 }
        },
        grid: {
          left: '5%',
          right: '5%',
          top: '24%',
          bottom: '12%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: times,
          axisLabel: {
            rotate: 45,
            color: '#94a3b8',
            fontSize: 10
          }
        },
        yAxis: {
          type: 'value',
          name: '成交量(手)',
          nameTextStyle: { color: '#94a3b8', fontSize: 11 },
          axisLabel: { color: '#94a3b8', fontSize: 10 },
          splitLine: { lineStyle: { color: 'rgba(99, 102, 241, 0.1)' } }
        },
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { type: 'slider', start: 0, end: 100, height: 15, bottom: 5 }
        ],
        series: [
          {
            name: '买盘',
            type: 'line',
            data: buyVolumes,
            smooth: true,
            showSymbol: false,
            itemStyle: { color: '#ef5350' },
            lineStyle: { color: '#ef5350', width: 2 },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(239, 83, 80, 0.3)' },
                  { offset: 1, color: 'rgba(239, 83, 80, 0.02)' }
                ]
              }
            }
          },
          {
            name: '卖盘',
            type: 'line',
            data: sellVolumes,
            smooth: true,
            showSymbol: false,
            itemStyle: { color: '#26a69a' },
            lineStyle: { color: '#26a69a', width: 2 },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(38, 166, 154, 0.3)' },
                  { offset: 1, color: 'rgba(38, 166, 154, 0.02)' }
                ]
              }
            }
          },
          {
            name: '中性盘',
            type: 'line',
            data: neutralVolumes,
            smooth: true,
            showSymbol: false,
            itemStyle: { color: '#78909c' },
            lineStyle: { color: '#78909c', width: 2 }
          }
        ]
      }

      this.mergedVolumeChart.setOption(option)
    },

    renderVolumeCharts(times, seriesData) {
      // 按买卖方向分组系列数据
      const buySeries = []
      const sellSeries = []
      const neutralSeries = []

      seriesData.forEach(item => {
        const seriesItem = {
          name: item.series,
          type: 'line',
          data: item.data,
          smooth: true,
          showSymbol: false
        }

        if (item.series.includes('买盘')) {
          buySeries.push(seriesItem)
        } else if (item.series.includes('卖盘')) {
          sellSeries.push(seriesItem)
        } else {
          neutralSeries.push(seriesItem)
        }
      })

      // 渲染买盘图表
      this.renderDirectionChart('buyChartContainer', 'buyChart', '买盘', times, buySeries, '#ef5350', 'volume')

      // 渲染卖盘图表
      this.renderDirectionChart('sellChartContainer', 'sellChart', '卖盘', times, sellSeries, '#26a69a', 'volume')

      // 渲染中性盘图表
      this.renderDirectionChart('neutralChartContainer', 'neutralChart', '中性盘', times, neutralSeries, '#78909c', 'volume')
    },

    renderCountCharts(times, seriesData) {
      // 按买卖方向分组系列数据
      const buySeries = []
      const sellSeries = []
      const neutralSeries = []

      seriesData.forEach(item => {
        const seriesItem = {
          name: item.series,
          type: 'line',
          data: item.data,
          smooth: true,
          showSymbol: false
        }

        if (item.series.includes('买盘')) {
          buySeries.push(seriesItem)
        } else if (item.series.includes('卖盘')) {
          sellSeries.push(seriesItem)
        } else {
          neutralSeries.push(seriesItem)
        }
      })

      // 渲染买盘成交次数图表
      this.renderDirectionChart('buyCountChartContainer', 'buyCountChart', '买盘', times, buySeries, '#ef5350', 'count')

      // 渲染卖盘成交次数图表
      this.renderDirectionChart('sellCountChartContainer', 'sellCountChart', '卖盘', times, sellSeries, '#26a69a', 'count')

      // 渲染中性盘成交次数图表
      this.renderDirectionChart('neutralCountChartContainer', 'neutralCountChart', '中性盘', times, neutralSeries, '#78909c', 'count')
    },

    // 生成颜色变体 - 使用醒目的预设颜色循环
    generateColorVariants(baseColor, count) {
      // 醒目的颜色方案
      const buyColors = [
        '#ff1744', '#d50000', '#f50057', '#c51162',
        '#e91e63', '#ad1457', '#ff5722', '#bf360c',
        '#ff9100', '#ff6d00', '#ff3d00', '#dd2c00'
      ]
      const sellColors = [
        '#00e676', '#00c853', '#69f0ae', '#00e676',
        '#1de9b6', '#00bfa5', '#00bcd4', '#0097a7',
        '#26c6da', '#00acc1', '#4dd0e1', '#0097a7'
      ]
      const neutralColors = [
        '#607d8b', '#455a64', '#78909c', '#546e7a',
        '#37474f', '#90a4ae', '#b0bec5', '#cfd8dc',
        '#eceff1', '#546e7a', '#78909c', '#90a4ae'
      ]

      let colorPalette
      if (baseColor === '#ef5350') {
        colorPalette = buyColors
      } else if (baseColor === '#26a69a') {
        colorPalette = sellColors
      } else {
        colorPalette = neutralColors
      }

      const colors = []
      for (let i = 0; i < count; i++) {
        colors.push(colorPalette[i % colorPalette.length])
      }
      return colors
    },

    // HSL转十六进制颜色
    hslToHex(h, s, l) {
      l /= 100
      const a = s * Math.min(l, 1 - l) / 100
      const f = n => {
        const k = (n + h / 30) % 12
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
        return Math.round(255 * color).toString(16).padStart(2, '0')
      }
      return `#${f(0)}${f(8)}${f(4)}`
    },

    // 十六进制转RGBA
    hexToRgba(hex, alpha) {
      let r, g, b
      if (hex.startsWith('#')) {
        hex = hex.slice(1)
      }
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16)
        g = parseInt(hex[1] + hex[1], 16)
        b = parseInt(hex[2] + hex[2], 16)
      } else {
        r = parseInt(hex.slice(0, 2), 16)
        g = parseInt(hex.slice(2, 4), 16)
        b = parseInt(hex.slice(4, 6), 16)
      }
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },

    renderDirectionChart(containerRef, chartRef, direction, times, series, baseColor, dataType) {
      const container = this.$refs[containerRef]
      if (!container) {
        console.log(`${containerRef} 不存在`)
        return
      }

      const chart = this[chartRef]
      if (chart) {
        chart.dispose()
      }

      this[chartRef] = echarts.init(container)

      // 为每个系列生成不同的颜色
      const colorVariants = this.generateColorVariants(baseColor, series.length)
      const processedSeries = series.map((item, index) => {
        const seriesColor = colorVariants[index]
        return {
          ...item,
          itemStyle: { color: seriesColor },
          lineStyle: { color: seriesColor, width: index === 0 ? 2 : 1.5 },
          areaStyle: index === 0 ? {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: this.hexToRgba(seriesColor, 0.2) },
                { offset: 1, color: this.hexToRgba(seriesColor, 0.02) }
              ]
            }
          } : null
        }
      })

      const yAxisLabel = dataType === 'volume' ? '成交量(手)' : '成交次数(笔)'

      const option = {
        title: {
          text: `${this.stockCode} ${direction}${dataType === 'volume' ? '成交量' : '成交次数'}走势${this.chartData.date ? ' - ' + this.chartData.date : ''}`,
          left: 'center',
          textStyle: { color: '#e2e8f0', fontSize: 14 }
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          formatter: (params) => {
            if (!params || params.length === 0) return ''
            let result = `${params[0].axisValue}<br/>`
            params.forEach(param => {
              if (param.value > 0) {
                result += `${param.marker} ${param.seriesName}: ${param.value} ${dataType === 'volume' ? '手' : '笔'}<br/>`
              }
            })
            return result
          }
        },
        legend: {
          data: series.map(s => s.name),
          top: 25,
          textStyle: { color: '#f1f5f9', fontSize: 12 },
          type: 'scroll'
        },
        grid: {
          left: '8%',
          right: '5%',
          top: '18%',
          bottom: '12%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: times,
          axisLabel: {
            rotate: 45,
            color: '#94a3b8',
            fontSize: 10
          }
        },
        yAxis: {
          type: 'value',
          name: yAxisLabel,
          nameTextStyle: { color: '#94a3b8', fontSize: 11 },
          axisLabel: { color: '#94a3b8', fontSize: 10 },
          splitLine: { lineStyle: { color: 'rgba(99, 102, 241, 0.1)' } }
        },
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { type: 'slider', start: 0, end: 100, height: 15, bottom: 5 }
        ],
        series: processedSeries
      }

      this[chartRef].setOption(option)
    },

    handleResize() {
      if (this.mergedVolumeChart) this.mergedVolumeChart.resize()
      if (this.buyChart) this.buyChart.resize()
      if (this.sellChart) this.sellChart.resize()
      if (this.neutralChart) this.neutralChart.resize()
      if (this.buyCountChart) this.buyCountChart.resize()
      if (this.sellCountChart) this.sellCountChart.resize()
      if (this.neutralCountChart) this.neutralCountChart.resize()
    },

    handlePageChange(page) {
      this.currentPage = page
    },

    resetFilters() {
      this.stockCode = '000001'
      this.date = ''
      this.batchMode = false
      this.startDate = ''
      this.endDate = ''
      this.batchData = []
      this.chartData = null
      this.currentPage = 1
      this.selectedDateIndex = 0
      this.directionFilter = '全部'
      if (this.mergedVolumeChart) {
        this.mergedVolumeChart.dispose()
        this.mergedVolumeChart = null
      }
      if (this.buyChart) {
        this.buyChart.dispose()
        this.buyChart = null
      }
      if (this.sellChart) {
        this.sellChart.dispose()
        this.sellChart = null
      }
      if (this.neutralChart) {
        this.neutralChart.dispose()
        this.neutralChart = null
      }
      if (this.buyCountChart) {
        this.buyCountChart.dispose()
        this.buyCountChart = null
      }
      if (this.sellCountChart) {
        this.sellCountChart.dispose()
        this.sellCountChart = null
      }
      if (this.neutralCountChart) {
        this.neutralCountChart.dispose()
        this.neutralCountChart = null
      }
    },

    formatVolume(value) {
      if (!value) return '0'
      const num = parseFloat(value)
      return num.toLocaleString()
    },

    formatAmount(value) {
      if (!value) return '0'
      const num = parseFloat(value)
      return num.toLocaleString()
    },

    getDirectionType(direction) {
      if (!direction) return 'info'
      if (direction.includes('买') || direction === 'B') return 'danger'
      if (direction.includes('卖') || direction === 'S') return 'success'
      return 'info'
    }
  },
  beforeDestroy() {
    if (this.mergedVolumeChart) {
      this.mergedVolumeChart.dispose()
      this.mergedVolumeChart = null
    }
    if (this.buyChart) {
      this.buyChart.dispose()
      this.buyChart = null
    }
    if (this.sellChart) {
      this.sellChart.dispose()
      this.sellChart = null
    }
    if (this.neutralChart) {
      this.neutralChart.dispose()
      this.neutralChart = null
    }
    if (this.buyCountChart) {
      this.buyCountChart.dispose()
      this.buyCountChart = null
    }
    if (this.sellCountChart) {
      this.sellCountChart.dispose()
      this.sellCountChart = null
    }
    if (this.neutralCountChart) {
      this.neutralCountChart.dispose()
      this.neutralCountChart = null
    }
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style scoped>
.bill-tick {
  padding: 20px;
  min-height: calc(100vh - 104px);
}

.page-header {
  text-align: center;
  margin-bottom: 32px;
  position: relative;
  z-index: 1;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 8px;
}

.title-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6ee7b7;
  border: 1px solid rgba(16, 185, 129, 0.3);
  box-shadow: 0 0 20px rgba(16, 185, 129, 0.15);
}

.title-icon svg {
  width: 24px;
  height: 24px;
}

.page-title h1 {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(135deg, #6ee7b7 0%, #34d399 50%, #6ee7b7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 1px;
}

.title-decoration {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, transparent 0%, #10b981 50%, transparent 100%);
  border-radius: 2px;
}

.page-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  letter-spacing: 0.5px;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
  position: relative;
  z-index: 1;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 180px;
}

.filter-label {
  font-size: 14px;
  color: #94a3b8;
  font-weight: 500;
}

.input-code {
  width: 200px;
}

.input-code :deep(.el-input__inner) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #e2e8f0;
  border-radius: 8px;
}

.stock-select {
  width: 250px;
}

.stock-select :deep(.el-input__inner) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #e2e8f0;
  border-radius: 8px;
}

.date-picker {
  width: 180px;
}

.date-picker :deep(.el-input__inner) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #e2e8f0;
  border-radius: 8px;
}

.date-picker :deep(.el-input__prefix) {
  color: #94a3b8;
}

.date-picker :deep(.el-input__icon) {
  color: #94a3b8;
}

.button-group {
  display: flex;
  gap: 10px;
  margin-left: auto;
}

.button-group :deep(.el-button--primary) {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3);
}

.button-group :deep(.el-button--default) {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 24px;
  color: #cbd5e1;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  background: rgba(15, 23, 42, 0.6);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid rgba(16, 185, 129, 0.15);
  text-align: center;
}

.stat-label {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #e2e8f0;
}

.batch-section {
  margin-bottom: 20px;
}

.date-tabs {
  margin-bottom: 15px;
}

.date-tabs :deep(.el-radio-group) {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.day-stats {
  background: rgba(15, 23, 42, 0.6);
  padding: 15px;
  border-radius: 8px;
  border: 1px solid rgba(16, 185, 129, 0.15);
}

.day-stat-item {
  color: #94a3b8;
  margin-right: 20px;
}

.day-stat-item span {
  color: #e2e8f0;
  font-weight: bold;
}

.charts-row {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.chart-item {
  flex: 1;
  height: 400px;
  background: rgba(15, 23, 42, 0.6);
  border-radius: 12px;
  border: 1px solid rgba(16, 185, 129, 0.15);
  padding: 15px;
}

.chart-container {
  height: 600px;
  background: rgba(15, 23, 42, 0.6);
  border-radius: 12px;
  border: 1px solid rgba(16, 185, 129, 0.15);
  padding: 20px;
  margin-bottom: 20px;
}

.table-section {
  background: rgba(15, 23, 42, 0.6);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(16, 185, 129, 0.15);
}

.table-section h3 {
  margin: 0;
  font-size: 18px;
  color: #e2e8f0;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  gap: 20px;
}

.table-filter {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-label-text {
  font-size: 14px;
  color: #94a3b8;
}

.table-filter :deep(.el-radio-group) {
  background: rgba(15, 23, 42, 0.6);
  border-radius: 8px;
  padding: 4px;
}

.table-filter :deep(.el-radio-button__inner) {
  background: transparent;
  border: none;
  color: #94a3b8;
  padding: 8px 16px;
  border-radius: 6px;
  transition: all 0.3s;
}

.table-filter :deep(.el-radio-button__orig-radio:checked + .el-radio-button__inner) {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.table-filter :deep(.el-radio-button__orig-radio:checked + .el-radio-button__inner:hover) {
  color: #ffffff;
}


.table-section :deep(.el-table) {
  background: rgba(15, 23, 42, 0.5);
}

.table-section :deep(.el-table__header-wrapper) {
  background: rgba(30, 41, 59, 0.9);
}

.table-section :deep(.el-table__header th) {
  background: rgba(30, 41, 59, 0.9) !important;
  color: #cbd5e1;
  font-weight: 600;
  border-bottom: 1px solid rgba(16, 185, 129, 0.2);
}

.table-section :deep(.el-table__body tr) {
  background: transparent;
}

/* 斑马纹样式 - 隔行使用深色 */
.table-section :deep(.el-table__row--striped) {
  background: rgba(30, 41, 59, 0.5) !important;
}

.table-section :deep(.el-table__row--striped td) {
  background: transparent !important;
}

.table-section :deep(.el-table__body tbody tr:hover > td) {
  background: rgba(16, 185, 129, 0.08) !important;
}

.table-section :deep(.el-table__body td) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  color: #e2e8f0;
}

.pagination {
  margin-top: 20px;
  text-align: center;
}

.pagination :deep(.el-pagination) {
  color: #94a3b8;
}

.pagination :deep(.el-pagination .el-pager li) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  margin: 0 4px;
}

.pagination :deep(.el-pagination .el-pager li.active) {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-color: transparent;
  color: #ffffff;
}

.pagination :deep(.el-pagination button) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #94a3b8;
}

:deep(.el-switch) {
  margin-top: 8px;
}

:deep(.el-switch__label) {
  color: #94a3b8;
}

:deep(.el-switch.is-checked .el-switch__core) {
  background-color: #10b981;
  border-color: #10b981;
}

:deep(.el-tag--danger) {
  background-color: rgba(239, 83, 80, 0.2);
  border-color: rgba(239, 83, 80, 0.4);
  color: #ef5350;
}

:deep(.el-tag--success) {
  background-color: rgba(38, 166, 154, 0.2);
  border-color: rgba(38, 166, 154, 0.4);
  color: #26a69a;
}

:deep(.el-tag--info) {
  background-color: rgba(120, 144, 156, 0.2);
  border-color: rgba(120, 144, 156, 0.4);
  color: #78909c;
}

.charts-section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 3px solid #10b981;
}
</style>
